
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Eye, Database, Clock, User, Shield } from "lucide-react";
import { format } from "date-fns";

interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  operation: string;
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  changed_by: string;
  created_at: string;
}

export function AuditLogViewer() {
  const { data: logs, isLoading } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as AuditLog[];
    },
  });

  if (isLoading) {
    return <div>Loading audit logs...</div>;
  }

  const getOperationColor = (operation: string) => {
    switch (operation.toUpperCase()) {
      case 'INSERT':
        return 'text-green-600';
      case 'UPDATE':
        return 'text-blue-600';
      case 'DELETE':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatData = (data: Record<string, any> | null) => {
    if (!data) return 'No data';
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join('\n');
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Database className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Audit Logs</h2>
      </div>
      <ScrollArea className="h-[600px] w-full rounded-md border">
        <div className="space-y-4 p-4">
          {logs?.map((log) => (
            <div
              key={log.id}
              className="border rounded-lg p-4 space-y-2 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span className="font-semibold">{log.table_name}</span>
                  <span className={`px-2 py-0.5 rounded text-sm ${getOperationColor(log.operation)}`}>
                    {log.operation}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="h-4 w-4" />
                <span>Changed by: {log.changed_by || 'System'}</span>
              </div>
              
              {log.operation === 'UPDATE' && (
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <div className="text-sm font-medium mb-1">Old Data:</div>
                    <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                      {formatData(log.old_data)}
                    </pre>
                  </div>
                  <div>
                    <div className="text-sm font-medium mb-1">New Data:</div>
                    <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                      {formatData(log.new_data)}
                    </pre>
                  </div>
                </div>
              )}
              
              {log.operation === 'INSERT' && (
                <div>
                  <div className="text-sm font-medium mb-1">New Data:</div>
                  <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                    {formatData(log.new_data)}
                  </pre>
                </div>
              )}
              
              {log.operation === 'DELETE' && (
                <div>
                  <div className="text-sm font-medium mb-1">Deleted Data:</div>
                  <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                    {formatData(log.old_data)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}
